import { defineStore } from 'pinia';
import { LocalStorage, SessionStorage } from 'quasar';
import { notify } from 'src/components/notifications/notify';
import {
  clearPersistedApiRegion,
  setApiRegion,
} from 'src/pages/api-base-url';
import { authSdkService } from 'src/services/user/authSdkService';
import cache from 'src/utils/cache';
import { getOrgId } from 'src/utils/organization/org_id_helper';
import { tol } from 'src/boot/tolgee';

export interface TokenDetails {
  access_token: string;
  refresh_token: string;
  expire_time: Date;
  expires_in: number;
  token_type: string;
  scope?: string;
}

export const useAuthTokenStore = defineStore('auth_token', {
  state: () => ({
    token: '',
    tokenPromise: null as any,
    // True while a refresh is actually in flight. Used to dedupe concurrent
    // callers into ONE refresh independent of the 10-minute cooldown below.
    refreshInFlight: false,
    tokenQueryParam: '',
    nextAllowedRefreshTime: 0,
    ignore_token_validation: false,
  }),
  getters: {
    getData(state) {
      return state;
    },
    current_token(state) {
      return state.token;
    },
  },
  actions: {
    async setAuthToken(token?: string) {
      try {
        // IMPORTANT: this.token must ALWAYS store the raw JWT without "Bearer " prefix.
        // The "Bearer " prefix is a transport concern added by getAuthorizationToken()
        // and HTTP interceptors — it must never be stored in state.
        const rawToken = token
          ? (token.startsWith('Bearer ') ? token.slice(7) : token)
          : await this.getValidatedToken();
        this.token = rawToken;
      } catch (err: any) {
        throw err;
      }
    },

    async getAuthorizationToken() {
      try {
        const token = await this.getValidatedToken();
        if (!token) {
          // ✅ FIX: Don't trigger redirectToLogin() here - it causes reload cascade during initialization
          // API service callers should handle missing token gracefully
          console.warn('[AUTH] ⚠️ No valid token available for authorization');
          return '';
        }

        return 'Bearer ' + token;
      } catch (err: any) {
        throw err;
      }
    },

    async getTokenQueryParam() {
      const token = await this.getValidatedToken();
      if (!token) {
        this.redirectToLogin();
        return '';
      }
      const orgId = getOrgId();
      this.tokenQueryParam = 'token=' + token + '&org_id=' + orgId;
    },

    async isLoggedIn() {
      if (this.isTokenExpired()) {
        const response = await this.refreshToken();
        if (!response) {
          // ✅ FIX: When token refresh fails, DON'T redirect here
          // The router guard will handle redirects appropriately based on the route
          // Calling redirectToLogin() here causes infinite loops on public routes
          console.warn('[AUTH] Token refresh failed - user not authenticated');
          return false;
        }
      }

      return true;
    },

    async getValidatedToken() {
      console.debug('[AuthToken] getValidatedToken called', {
        hasToken: !!this.token,
        tokenLength: this.token?.length || 0,
        isExpired: this.isTokenExpired(),
        ignoreValidation: this.ignore_token_validation,
      });

      if (this.ignore_token_validation) {
        return this.token;
      }

      if (this.isTokenExpired()) {
        console.log('[AuthToken] Token expired, attempting refresh...');
        const response = await this.refreshToken();
        console.log('[AuthToken] Token refresh result:', {
          success: !!response,
          hasNewToken: !!this.token,
        });
        if (!response) {
          console.error('[AuthToken] Token refresh failed - returning empty string');
          return '';
        }
      }

      const tokenInfo: any = this.getTokenDetails();
      console.debug('[AuthToken] Retrieved token details:', {
        hasTokenInfo: !!tokenInfo,
        hasAccessToken: !!(tokenInfo && tokenInfo.access_token),
      });

      const token =
        tokenInfo && tokenInfo.access_token
          ? String(tokenInfo.access_token)
          : '';

      console.debug('[AuthToken] Returning token:', {
        hasToken: !!token,
        tokenLength: token.length,
      });

      return token;
    },

    isTokenExpired() {
      const tokenInfo: any = this.getTokenDetails();
      let isExpired = true;
      if (!tokenInfo || !tokenInfo.access_token) {
        console.warn('[AUTH] 🔴 Token check: No token found in storage');
        return isExpired;
      }

      const now = new Date();
      const expiresAt = new Date(tokenInfo.expire_time);

      if (now < expiresAt) {
        isExpired = false;
        const minutesRemaining = Math.floor((expiresAt.getTime() - now.getTime()) / 1000 / 60);
        console.debug(`[AUTH] 🟢 Token valid - expires in ${minutesRemaining} minutes (${expiresAt.toISOString()})`);
      } else {
        const minutesExpired = Math.floor((now.getTime() - expiresAt.getTime()) / 1000 / 60);
        console.warn(`[AUTH] 🔴 Token EXPIRED - was valid ${minutesExpired} minutes ago (expired at ${expiresAt.toISOString()})`);
      }

      return isExpired;
    },

    redirectToLogin() {
      // CRITICAL: Don't redirect if we're on prepare-dashboard page
      // Let the component handle the error gracefully instead of silent redirect
      if (
        typeof window !== 'undefined' &&
        window.location.pathname.includes('/prepare-dashboard')
      ) {
        console.warn(
          '[AUTH] Token refresh failed on prepare-dashboard page - allowing error handling in component',
        );
        return; // Don't redirect - let PrepareDashboard handle the error
      }
      cache.methods.logout();
    },

    async tryToRefreshToken(reload: boolean) {
      const istokenRefresh = await this.refreshToken();
      if (!istokenRefresh) {
        this.redirectToLogin();
        return;
      }

      if (reload) {
        // Don't reload if we're on prepare-dashboard page (let it handle errors)
        if (
          typeof window !== 'undefined' &&
          !window.location.pathname.includes('/prepare-dashboard')
        ) {
          window.location.reload();
        } else {
          console.log(
            'Skipping auth reload on prepare-dashboard to allow error display',
          );
        }
      }
    },

    refreshToken(): Promise<boolean> {
      const now = Date.now();

      // SINGLE-FLIGHT: if a refresh is actually in flight, every concurrent
      // caller shares the SAME promise. This is the canonical refresh used by
      // BOTH HTTP stacks — the generated SDK's mutator (custom-axios-instance,
      // via refreshAccessToken → authStore.refreshToken) and the legacy
      // ApiClient (via store.tryToRefreshToken → this.refreshToken) — plus
      // getAuthorizationToken. Without this guard, a page firing parallel
      // requests on an expired access token could start two refreshes with the
      // same refresh_token; the second presents the just-rotated (now-revoked)
      // token and trips the auth server's replay/breach detection, which
      // revokes the whole token family and bounces the user to /login.
      // Note: the in-flight guard is decoupled from the 10-minute cooldown
      // below on purpose — the cooldown rate-limits NEW refreshes, the
      // in-flight guard dedupes SIMULTANEOUS ones.
      if (this.refreshInFlight && this.tokenPromise) {
        return this.tokenPromise;
      }

      // 10-minute cooldown: within the window after a refresh, reuse the last
      // (already settled) promise instead of re-hitting the auth server.
      if (now < this.nextAllowedRefreshTime && this.tokenPromise) {
        return this.tokenPromise;
      }

      // Set cooldown time
      this.nextAllowedRefreshTime = now + 10 * 60 * 1000;

      // Execute and store the promise. Clear refreshInFlight when it settles so
      // the NEXT genuine refresh can run, while all callers DURING this one
      // dedupe to this promise.
      this.refreshInFlight = true;
      this.tokenPromise = (async () => {
        try {
          return await this.refreshAcrossTabs();
        } finally {
          this.refreshInFlight = false;
        }
      })();

      return this.tokenPromise;
    },

    /**
     * CROSS-TAB SINGLE-FLIGHT. The in-flight guard in refreshToken() only
     * dedupes refreshes WITHIN one tab. Two tabs sharing the same login each
     * have their own store, so both could refresh the same refresh_token at
     * once; the loser presents the just-rotated (now-revoked) token and trips
     * the auth server's replay/breach detection — revoking the whole token
     * family and bouncing every tab to /login.
     *
     * Web Locks gives a cross-tab mutex keyed on the shared origin: only one tab
     * holds 'docbits-token-refresh' at a time. A tab that waited for the lock
     * re-reads localStorage (written by the tab that just refreshed) and, if the
     * token is valid again, skips its own refresh entirely. Falls back to a
     * direct refresh where navigator.locks is unavailable (older browsers /
     * insecure context) — there the server-side 30s rotation grace still covers
     * the race.
     */
    async refreshAcrossTabs(): Promise<boolean> {
      // Safe validity check — does NOT reuse isTokenExpired() because that logs
      // expiresAt.toISOString(), which throws RangeError on a missing/invalid
      // expire_time. Here we only want a boolean.
      const storedTokenStillValid = (): boolean => {
        try {
          const t: any = this.getTokenDetails();
          if (!t?.access_token || !t?.expire_time) return false;
          const expMs = new Date(t.expire_time).getTime();
          return Number.isFinite(expMs) && Date.now() < expMs;
        } catch {
          return false;
        }
      };

      const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
      if (locks && typeof locks.request === 'function') {
        // Hold the cross-tab mutex while we refresh. The bail lives INSIDE the
        // lock because it only matters AFTER we may have waited for another
        // tab's refresh — a tab that waited re-reads shared localStorage and
        // skips its own (redundant) refresh if the token is valid again.
        //
        // No catch-and-rerun: a rejection from request() is the refresh itself
        // failing inside the callback, and rerunning would double-fire it. In a
        // secure context navigator.locks is always present and usable; insecure
        // contexts expose no navigator.locks at all (handled by the fallback).
        return await locks.request('docbits-token-refresh', async () => {
          if (storedTokenStillValid()) {
            console.log(
              '[AUTH] Token already fresh (refreshed by another tab) — skipping refresh',
            );
            return true;
          }
          return await this.fetchFreshToken();
        });
      }

      // No Web Locks (insecure context / SSR / old browser): no cross-tab wait,
      // so there is nothing to bail on — refresh directly, exactly as before
      // this method existed. The server-side 30s rotation grace still covers a
      // multi-tab race here.
      return await this.fetchFreshToken();
    },

    // Do not use this method directly
    async fetchFreshToken() {
      const value: any = this.getTokenDetails();

      // SOLUTION B - STEP 1: Check if we're offline
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        console.warn(
          '[AUTH] Offline - skipping token refresh, will retry when online',
        );
        notify.warning(
          tol.t('offline_mode_active') ||
            'You are offline. Token refresh will be retried when connection is restored.',
        );
        return false; // Don't logout when offline
      }

      if (!value || !value.access_token) {
        const isIntentionalLogout =
          SessionStorage.getItem('intentional_logout');
        const hadPreviousSession = !!LocalStorage.getItem('user_details');

        // Clear the intentional logout flag after checking it
        SessionStorage.remove('intentional_logout');

        // NOTE: Notification removed - WebSocket notification service handles this
        // via ConnectionNotificationService.notifyFailed() with error.message
        // See: src/services/notifications/ConnectionNotificationService.ts:149-185
        // This prevents duplicate notifications when session expires
        if (!isIntentionalLogout && hadPreviousSession) {
          console.error('[AUTH] Session expired - no valid token found');
          // Notification will be shown by WebSocket connection service
        } else if (isIntentionalLogout) {
          console.log('[AUTH] User logged out intentionally');
        } else {
          console.log('[AUTH] No token found - user not logged in');
        }

        return false;
      }

      // SOLUTION B - STEP 2: Retry logic with exponential backoff
      const MAX_RETRIES = 3;
      let lastError: any = null;

      // ✅ CRITICAL: Validate refresh_token exists before attempting refresh
      // This prevents calling authSdkService.refreshToken(undefined) which fails silently
      if (!value.refresh_token) {
        console.error('[AUTH] ❌ CRITICAL: refresh_token is missing from stored token');
        console.error('[AUTH] Stored token keys:', value ? Object.keys(value) : []);
        console.error('[AUTH] This indicates login response did not include refresh_token');
        notify.error('Session cannot be refreshed. Please log in again.');
        return false;
      }

      // Generate unique refresh ID to track concurrent refresh attempts
      const refreshId = `refresh_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
      console.log(`[AUTH] 🔄 Starting token refresh ${refreshId}`);
      console.log(`[AUTH] refresh_token present: ${value.refresh_token ? '✓ (length: ' + value.refresh_token.length + ')' : '❌ MISSING'}`);

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          // Re-read token details on each attempt to pick up rotated refresh_token
          // After a successful refresh, the old refresh_token is invalidated (OAuth2 token rotation).
          // Retrying with a stale refresh_token triggers TOKEN_BREACH_DETECTED on the auth server.
          const currentToken = attempt > 1 ? this.getTokenDetails() : value;
          const currentRefreshToken = currentToken?.refresh_token || value.refresh_token;

          console.log(
            `[AUTH] ${refreshId} - Refreshing token (attempt ${attempt}/${MAX_RETRIES})`,
          );

          const response = await authSdkService.refreshToken(currentRefreshToken);

          if (!response || !response.access_token) {
            throw new Error(
              'Invalid refresh response - no access_token in response',
            );
          }

          console.log(`[AUTH] ${refreshId} - ✅ Token refresh SUCCESS on attempt ${attempt}`);
          console.log(`[AUTH] ${refreshId} - New access_token received: ${response.access_token ? '✓' : '❌'}`);
          console.log(`[AUTH] ${refreshId} - New refresh_token received: ${response.refresh_token ? '✓' : '❌'}`);
          this.setTokenInLocalStorage(response);

          // Broadcast token update to other tabs
          //this.broadcastTokenUpdate(response);

          // Only show success notification on first attempt or after retry
          if (attempt > 1) {
            notify.success(
              tol.t('session_refreshed_success') ||
                'Session refreshed successfully',
            );
          }

          return true;
        } catch (err: any) {
          lastError = err;
          console.error(
            `[AUTH] ${refreshId} - ❌ Token refresh attempt ${attempt}/${MAX_RETRIES} FAILED`,
          );
          console.error(`[AUTH] ${refreshId} - Error type:`, typeof err);
          console.error(`[AUTH] ${refreshId} - Error message:`, err?.message || err);
          console.error(`[AUTH] ${refreshId} - Error body:`, err?.body);
          console.error(`[AUTH] ${refreshId} - Error status:`, err?.response?.status || err?.status);
          console.error(`[AUTH] ${refreshId} - Full error:`, err);
          logError(err);

          // Don't retry on permanent auth failures — the refresh token is invalid/expired.
          // Check HTTP status (err.response?.status for axios, err.status for ApiError)
          // and OAuth2 error codes (err.error for re-thrown error.body from authSdkService)
          const errStatus = err.response?.status || err.status;
          const oauthError = err.error || err.body?.error;
          if (errStatus === 403 || errStatus === 401 || oauthError === 'invalid_grant') {
            console.error(
              `[AUTH] ${refreshId} - Authentication error - not retrying (403/401)`,
            );
            notify.error(
              tol.t('authentication_error') ||
                'Authentication failed. Please log in again.',
            );
            break; // Exit retry loop
          }

          // Don't retry on invalid_grant — the refresh_token was already rotated or revoked.
          // Retrying with the same (or even a re-read) token would trigger TOKEN_BREACH_DETECTED.
          const errCode = err?.error || err?.body?.error;
          if (errCode === 'invalid_grant' || errCode === 'refresh_token_replay') {
            console.error(
              `[AUTH] ${refreshId} - Refresh token rejected (${errCode}) - not retrying`,
            );
            break;
          }

          // Network error or server error - retry with backoff
          if (attempt < MAX_RETRIES) {
            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Max 5 seconds
            console.log(
              `[AUTH] ${refreshId} - Waiting ${delay}ms before retry ${attempt + 1}...`,
            );

            // Show retry notification on second attempt onwards
            if (attempt === 1) {
              notify.warning(
                tol.t('network_connection_issue') ||
                  'Connection issue detected. Retrying...',
              );
            }

            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }

      // All retries failed
      console.error(
        `[AUTH] ${refreshId} - ❌ Token refresh FAILED after ${MAX_RETRIES} attempts`,
      );
      console.error(`[AUTH] ${refreshId} - Last error:`, lastError);
      console.error(`[AUTH] ${refreshId} - Last error message:`, lastError?.message || lastError);
      console.error(`[AUTH] ${refreshId} - Last error body:`, lastError?.body);

      // Clear stale token so the root route guard and Login.vue don't think
      // the user is still authenticated (prevents redirect loop to /dashboard)
      LocalStorage.remove('token_details');
      this.token = '';
      setApiRegion('eu');
      clearPersistedApiRegion();

      notify.error(
        tol.t('session_refresh_failed_please_login') ||
          'Session refresh failed. Please log in again.',
      );

      return false;
    },

    setTokenInLocalStorage(data: any) {
      this.token = data.access_token;
      const t = new Date();
      const expires_in: number = +data.expires_in;

      // DEBUG: Log token response structure to understand what's being stored
      console.log('[AUTH] 🔍 Token Response Debug:');
      console.log('[AUTH]   - access_token:', data.access_token ? '✓' : '❌ MISSING');
      console.log('[AUTH]   - refresh_token:', data.refresh_token ? '✓' : '❌ MISSING');
      console.log('[AUTH]   - expires_in value:', data.expires_in);
      console.log('[AUTH]   - expires_in parsed:', expires_in);
      console.log('[AUTH]   - expires_in is valid?:', !isNaN(expires_in) && expires_in > 0);
      console.log('[AUTH]   - token_type:', data.token_type);
      console.log('[AUTH]   - scope:', data.scope);

      // CRITICAL FIX: Handle missing or invalid expires_in
      // If expires_in is missing, NaN, 0, or negative, use default 1 hour
      let effectiveExpiresIn = expires_in;
      if (isNaN(expires_in) || expires_in <= 0) {
        console.error('[AUTH] ❌ CRITICAL: expires_in is missing or invalid:', data.expires_in);
        console.error('[AUTH] Using default 1-hour expiration to prevent immediate session expiration');
        effectiveExpiresIn = 3600; // Default 1 hour if not provided
      }

      // FIXED: Use percentage-based buffer instead of fixed 600s
      // This prevents negative expiration for short-lived tokens
      // For tokens >= 1 day: use 98% of lifetime (2% buffer)
      // For tokens < 1 day: use 95% of lifetime (5% buffer)
      // NOTE: Increased buffer from 90% to 95% for short tokens to provide
      // additional safety margin during prepare-dashboard initialization (takes 15-20 seconds)
      // This gives a 1-hour token 57min validity instead of 54min (3 extra minutes)
      const oneDaySeconds = 86400;
      const effectiveExpiry =
        effectiveExpiresIn >= oneDaySeconds
          ? Math.floor(effectiveExpiresIn * 0.98)
          : Math.floor(effectiveExpiresIn * 0.95);

      console.log('[AUTH]   - effectiveExpiresIn:', effectiveExpiresIn);
      console.log('[AUTH]   - effectiveExpiry seconds:', effectiveExpiry);
      console.log('[AUTH]   - token expires at:', new Date(t.getTime() + effectiveExpiry * 1000).toISOString());

      t.setSeconds(t.getSeconds() + effectiveExpiry);
      data['expire_time'] = t;

      LocalStorage.set('token_details', data);
      LocalStorage.remove('login_attempts');

      // Log what was actually stored
      const stored = LocalStorage.getItem('token_details') as any;
      console.log('[AUTH] 📦 Stored in LocalStorage:');
      console.log('[AUTH]   - access_token:', stored?.access_token ? stored.access_token.substring(0, 20) + '...' : '❌ MISSING');
      console.log('[AUTH]   - refresh_token:', stored?.refresh_token ? stored.refresh_token.substring(0, 20) + '...' : '❌ MISSING');
      console.log('[AUTH]   - expire_time:', stored?.expire_time);
    },

    getTokenDetails(): TokenDetails | null {
      const tokenDetails = LocalStorage.getItem('token_details') as TokenDetails | null;
      console.debug('[AuthToken] getTokenDetails:', {
        hasTokenDetails: !!tokenDetails,
        tokenDetailsType: typeof tokenDetails,
        keys: tokenDetails ? Object.keys(tokenDetails) : [],
      });
      return tokenDetails;
    },

    /**
     * Hydrate Pinia state from LocalStorage.
     * Call this before any auth checks to ensure Pinia state is in sync with persisted storage.
     * This fixes race conditions where token exists in LocalStorage but Pinia state is empty
     * (e.g., after page refresh or direct navigation to protected routes).
     * @returns true if token was hydrated, false if no token found
     */
    hydrateFromLocalStorage(): boolean {
      const tokenDetails = LocalStorage.getItem('token_details') as TokenDetails | null;
      if (tokenDetails?.access_token && !this.token) {
        console.log('[AUTH] 🔄 Hydrating Pinia state from LocalStorage');
        this.token = tokenDetails.access_token;
        return true;
      }
      if (this.token) {
        console.log('[AUTH] ✓ Pinia state already has token');
        return true;
      }
      console.log('[AUTH] ⚠️ No token in LocalStorage to hydrate');
      return false;
    },

    isAdmin() {
      const userInfo: any = LocalStorage.getItem('user_details');
      // ✅ FIX: Add null check to prevent crash when localStorage is corrupted/empty
      if (userInfo && userInfo.is_organisation_admin) {
        return true;
      }
      return false;
    },
  },
});
